/**
 * PDP below-ATC tabs.
 * Preferred: sections/product-below-atc-tabs with data-pdp-tabs-apps-inline
 * (apps rendered in panels via @app — no relocate).
 * Legacy fallback: park hosts + relocate from main-product / apps section.
 */
(function () {
  const STOCK_SOURCE_SEL = '.product-form__location-stock';
  const PARK_CLASS = 'pdp-tab-source--parked';
  const REVIEW_HOST_SEL = '[data-pdp-tab-host="reviews"]';
  const MAX_REVIEW_RETRIES = 40;
  const REVIEW_RETRY_MS = 250;

  function qs(root, sel) {
    return (root || document).querySelector(sel);
  }

  function parkSource(el) {
    if (!el) return;
    el.classList.add(PARK_CLASS);
    el.classList.remove('pdp-tab-source--fallback');
    el.setAttribute('data-pdp-tab-source', 'parked');
    el.setAttribute('aria-hidden', 'true');
  }

  function unparkSource(el) {
    if (!el) return;
    el.classList.remove(PARK_CLASS);
    el.classList.add('pdp-tab-source--fallback');
    el.removeAttribute('data-pdp-tab-source');
    el.removeAttribute('aria-hidden');
  }

  /**
   * Shopify JSON sections use ids like:
   *   shopify-section-template--XXXX__mxpr-product-reviews
   * not shopify-section-mxpr-product-reviews.
   */
  function findReviewsSection() {
    return (
      qs(document, REVIEW_HOST_SEL) ||
      qs(document, '[id^="shopify-section-"][id*="mxpr-product-reviews"]') ||
      qs(document, '#shopify-section-mxpr-product-reviews')
    );
  }

  function findReviewsContainer(section) {
    if (!section) return null;
    return (
      qs(section, '[data-pdp-reviews-container]') ||
      qs(section, '.container') ||
      section
    );
  }

  function isPlaceholderNode(node) {
    return (
      node.nodeType === 1 &&
      (node.hasAttribute('data-pdp-tab-placeholder') ||
        node.classList.contains('product-below-atc-tabs__placeholder'))
    );
  }

  function mountHasReviewContent(mount) {
    if (!mount) return false;
    if (mount.getAttribute('data-pdp-tab-filled') === '1') {
      return !!mount.querySelector(
        '.shopify-app-block, .shopify-block, [id*="mxpr"], [class*="mxpr"]'
      );
    }
    return !!mount.querySelector(
      '.shopify-app-block, .shopify-block, [id*="mxpr"], [class*="mxpr"]'
    );
  }

  function clearFallbackOnly(mount) {
    if (!mount) return;
    mount.querySelectorAll('.product-below-atc-tabs__fallback').forEach(function (n) {
      n.remove();
    });
  }

  function relocateStock(root) {
    const mount = qs(root, '[data-pdp-tab-mount="stock"]');
    const source = qs(document, STOCK_SOURCE_SEL);
    if (!mount || !source) return false;

    if (mount.getAttribute('data-pdp-tab-filled') === '1' && mount.querySelector('.product-form__location-stock-app, [class*="lsi-"]')) {
      parkSource(source);
      return true;
    }

    const app = qs(source, '.product-form__location-stock-app') || source;
    const heading = qs(source, ':scope > .js-summary');
    if (heading) heading.setAttribute('hidden', '');

    if (mount.contains(app)) {
      parkSource(source);
      mount.setAttribute('data-pdp-tab-filled', '1');
      return true;
    }

    clearFallbackOnly(mount);
    mount.appendChild(app);
    parkSource(source);
    source.setAttribute('data-pdp-tab-relocated', 'stock');
    mount.setAttribute('data-pdp-tab-filled', '1');
    return true;
  }

  function relocateReviews(root) {
    const mount = qs(root, '[data-pdp-tab-mount="reviews"]');
    if (!mount) return false;

    // Already filled — never remount (prevents flicker / loading flash).
    if (mountHasReviewContent(mount)) {
      const section = findReviewsSection();
      if (section) parkSource(section);
      mount.setAttribute('data-pdp-tab-filled', '1');
      return true;
    }

    const section = findReviewsSection();
    if (!section) return false;

    const container = findReviewsContainer(section);
    const nodes = Array.prototype.slice.call(container.children).filter(function (child) {
      return !isPlaceholderNode(child);
    });

    if (!nodes.length) return false;

    clearFallbackOnly(mount);
    nodes.forEach(function (node) {
      mount.appendChild(node);
    });

    if (!qs(container, '[data-pdp-tab-placeholder="reviews"]')) {
      const ph = document.createElement('div');
      ph.setAttribute('data-pdp-tab-placeholder', 'reviews');
      ph.className = 'product-below-atc-tabs__placeholder';
      container.appendChild(ph);
    }

    parkSource(section);
    section.setAttribute('data-pdp-tab-relocated', 'reviews');
    mount.setAttribute('data-pdp-tab-filled', '1');
    return true;
  }

  function scheduleReviewsRetry(root) {
    if (root.dataset.pdpReviewsRetrying === '1') return;
    root.dataset.pdpReviewsRetrying = '1';
    var attempts = 0;

    function tick() {
      attempts += 1;
      if (relocateReviews(root)) {
        root.dataset.pdpReviewsRetrying = '0';
        return;
      }
      if (attempts >= MAX_REVIEW_RETRIES) {
        root.dataset.pdpReviewsRetrying = '0';
        // Leave host parked if present so we never show a duplicate below;
        // tab keeps the fallback line only if apps never appeared.
        return;
      }
      setTimeout(tick, REVIEW_RETRY_MS);
    }

    setTimeout(tick, REVIEW_RETRY_MS);
  }

  /**
   * Keep panels-area height across switches (CLS guard).
   * Reserve current height before toggle; settle to active panel after paint.
   */
  function reservePanelsHeight(root) {
    const panelsRoot = qs(root, '.product-below-atc-tabs__panels');
    if (!panelsRoot) return;
    var h = Math.ceil(panelsRoot.getBoundingClientRect().height);
    if (h > 0) panelsRoot.style.minHeight = h + 'px';
  }

  function measurePanelHeight(panel) {
    if (!panel) return 0;
    var rectH = Math.ceil(panel.getBoundingClientRect().height);
    var scrollH = Math.ceil(panel.scrollHeight || 0);
    return Math.max(rectH, scrollH);
  }

  function settlePanelsMinHeight(root) {
    const panelsRoot = qs(root, '.product-below-atc-tabs__panels');
    if (!panelsRoot) return;
    const active = qs(root, '[data-pdp-tab-panel]:not([hidden])');
    if (!active) return;
    // Absolute-stack active panels do not contribute to flow — always pin min-height.
    var h = measurePanelHeight(active);
    panelsRoot.style.minHeight = h > 0 ? h + 'px' : '4rem';
  }

  function scheduleSettlePanelsHeight(root) {
    if (root._pdpHeightRaf) cancelAnimationFrame(root._pdpHeightRaf);
    root._pdpHeightRaf = requestAnimationFrame(function () {
      root._pdpHeightRaf = null;
      settlePanelsMinHeight(root);
    });
  }

  function clearOutgoingOverlaps(root) {
    root.querySelectorAll('[data-pdp-tab-panel].is-outgoing').forEach(function (panel) {
      panel.classList.remove('is-outgoing');
    });
  }

  /**
   * Phase C: keep the previous panel painted on top until the next panel has
   * painted AND its height is settled (double rAF → settle → one more rAF).
   * Absolute-only stack (#311) removes relative↔absolute jumps; overlap still
   * covers the visibility hard-cut for Liquid and app panels alike.
   */
  function scheduleOutgoingOverlapEnd(root) {
    if (root._pdpOverlapRaf1) cancelAnimationFrame(root._pdpOverlapRaf1);
    if (root._pdpOverlapRaf2) cancelAnimationFrame(root._pdpOverlapRaf2);
    if (root._pdpOverlapRaf3) cancelAnimationFrame(root._pdpOverlapRaf3);
    root._pdpOverlapRaf1 = requestAnimationFrame(function () {
      root._pdpOverlapRaf1 = null;
      root._pdpOverlapRaf2 = requestAnimationFrame(function () {
        root._pdpOverlapRaf2 = null;
        // Settle height while outgoing still covers — avoids blank when cover lifts.
        settlePanelsMinHeight(root);
        root._pdpOverlapRaf3 = requestAnimationFrame(function () {
          root._pdpOverlapRaf3 = null;
          clearOutgoingOverlaps(root);
          settlePanelsMinHeight(root);
        });
      });
    });
  }

  function setPanelInactive(panel, options) {
    options = options || {};
    panel.classList.remove('is-active', 'is-inactive');
    if (!options.keepOutgoing) panel.classList.remove('is-outgoing');
    panel.style.removeProperty('opacity');
    panel.style.removeProperty('display');
    panel.hidden = true;
    panel.setAttribute('hidden', '');
    panel.setAttribute('aria-hidden', 'true');
    try {
      panel.inert = true;
    } catch (_err) {
      panel.setAttribute('inert', '');
    }
    if (options.keepOutgoing) panel.classList.add('is-outgoing');
  }

  function setPanelActive(panel) {
    panel.classList.remove('is-active', 'is-inactive', 'is-outgoing');
    panel.style.removeProperty('opacity');
    panel.style.removeProperty('display');
    panel.hidden = false;
    panel.removeAttribute('hidden');
    panel.removeAttribute('aria-hidden');
    try {
      panel.inert = false;
    } catch (_err2) {
      panel.removeAttribute('inert');
    }
  }

  /**
   * Enforce single visible panel without remounting content.
   * Contract still uses HTML `[hidden]` (#286/#296) — never is-inactive CSS.
   * Paint model is measure-preserving absolute stack for ALL panels (active
   * included — #311) so switches never relative↔absolute; apps keep boxes.
   * Phase C: outgoing panel keeps painting via .is-outgoing until height settles.
   */
  function syncPanelVisibility(root, activeButton, options) {
    options = options || {};
    const nav = qs(root, 'tabs-nav');
    const button =
      activeButton ||
      (nav && nav.selectedButton) ||
      qs(root, '.tabs-nav__item[aria-expanded="true"]');
    if (!button) return;

    const allowOverlap = options.overlap !== false;
    // TabsNav toggles [hidden] before our sync runs — prefer explicit outgoing
    // captured in the selectButton patch; fall back to still-visible / .is-outgoing.
    const previousVisible = [];
    if (options.outgoingPanel) previousVisible.push(options.outgoingPanel);
    root.querySelectorAll('[data-pdp-tab-panel]').forEach(function (panel) {
      if (previousVisible.indexOf(panel) !== -1) return;
      if (!panel.hidden || panel.classList.contains('is-outgoing')) {
        previousVisible.push(panel);
      }
    });

    // Lock absolute stack before any switch so incoming never goes relative↔absolute.
    if (allowOverlap && root.getAttribute('data-pdp-tabs-stacked') !== '1') {
      settlePanelsMinHeight(root);
      root.setAttribute('data-pdp-tabs-stacked', '1');
    }

    reservePanelsHeight(root);

    const activeName = button.getAttribute('data-pdp-tab');
    const activeId = button.getAttribute('aria-controls');
    var shown = 0;
    var incoming = null;

    root.querySelectorAll('[data-pdp-tab-panel]').forEach(function (panel) {
      const panelName = panel.getAttribute('data-pdp-tab-panel');
      const isActive =
        (activeName && panelName === activeName) ||
        (!!activeId && panel.id === activeId);
      if (isActive) {
        shown += 1;
        incoming = panel;
        setPanelActive(panel);
      }
    });

    if (!shown) {
      const fallback =
        qs(root, '[data-pdp-tab-panel="' + (root.getAttribute('data-default-tab') || 'description') + '"]') ||
        qs(root, '[data-pdp-tab-panel]');
      if (fallback) {
        incoming = fallback;
        setPanelActive(fallback);
        shown = 1;
      }
    }

    var usedOverlap = false;
    root.querySelectorAll('[data-pdp-tab-panel]').forEach(function (panel) {
      if (panel === incoming) return;
      const wasVisible = previousVisible.indexOf(panel) !== -1;
      const keepOutgoing = allowOverlap && wasVisible && !!incoming;
      if (keepOutgoing) usedOverlap = true;
      setPanelInactive(panel, { keepOutgoing: keepOutgoing });
    });

    if (usedOverlap) {
      scheduleOutgoingOverlapEnd(root);
    } else {
      clearOutgoingOverlaps(root);
      scheduleSettlePanelsHeight(root);
    }
  }

  /**
   * Nudge inline apps. Global window.resize only on first show (or force),
   * not every tab switch — measure-preserving hide keeps widget sizes.
   */
  function nudgeInlineApps(root, tabName, options) {
    options = options || {};
    if (root.getAttribute('data-pdp-tabs-apps-inline') !== 'true') return;
    const panel = qs(root, '[data-pdp-tab-panel="' + tabName + '"]');
    if (!panel || panel.hidden) return;

    if (!root._pdpNudgedTabs) root._pdpNudgedTabs = Object.create(null);
    var force = options.force === true;
    var firstShow = !root._pdpNudgedTabs[tabName];

    if (firstShow || force) {
      root._pdpNudgedTabs[tabName] = true;
      try {
        window.dispatchEvent(new Event('resize'));
      } catch (_err) {
        /* ignore */
      }
      panel.querySelectorAll('img').forEach(function (img) {
        if (typeof img.decode === 'function') {
          try {
            img.decode().catch(function () {});
          } catch (_e2) {
            /* ignore */
          }
        }
      });
    }

    // Image lightboxes must not stay inside the (possibly narrow / flex) panel.
    if (tabName === 'reviews') relocateMxprModals(root);
  }

  /**
   * Coalesce first-show nudges to one rAF per burst (#308 single-path).
   * options.force is kept for bfcache / section reload.
   */
  function scheduleNudgeInlineApps(root, tabName, options) {
    if (!tabName || root.getAttribute('data-pdp-tabs-apps-inline') !== 'true') return;
    if (root._pdpNudgeRaf) {
      cancelAnimationFrame(root._pdpNudgeRaf);
    }
    root._pdpNudgeTab = tabName;
    root._pdpNudgeOpts = options || null;
    root._pdpNudgeRaf = requestAnimationFrame(function () {
      root._pdpNudgeRaf = null;
      const name = root._pdpNudgeTab;
      const opts = root._pdpNudgeOpts;
      root._pdpNudgeTab = null;
      root._pdpNudgeOpts = null;
      if (name) nudgeInlineApps(root, name, opts || {});
    });
  }

  /**
   * Prime Review (mxpr) renders MicroModal lightboxes as siblings of the
   * thumbnail inside `.mxpr-review__list-item-image` (display:flex). Nested
   * under PDP tabs, the modal root collapses to width:0 and `position:fixed`
   * overlays size to the section instead of the viewport — the zoom image
   * then appears to slide upward (mmslideIn/Out) and vanish.
   * Move modals to document.body; triggers stay in the reviews panel.
   */
  function relocateMxprModals(root) {
    if (!document.body) return;
    const scopes = [];
    if (root) scopes.push(root);
    const parked =
      qs(document, REVIEW_HOST_SEL) ||
      qs(document, '[id^="shopify-section-"][id*="mxpr-product-reviews"]');
    if (parked && scopes.indexOf(parked) === -1) scopes.push(parked);

    scopes.forEach(function (scope) {
      scope.querySelectorAll('.mxpr-modal').forEach(function (modal) {
        if (!modal || modal.parentElement === document.body) return;
        try {
          modal.setAttribute('data-pdp-mxpr-modal-host', 'body');
          document.body.appendChild(modal);
        } catch (_err) {
          /* ignore */
        }
      });
    });
  }

  function observeMxprModals(root) {
    if (!root || root.dataset.pdpMxprModalObs === '1') return;
    root.dataset.pdpMxprModalObs = '1';
    const obs = new MutationObserver(function () {
      relocateMxprModals(root);
    });
    obs.observe(root, { childList: true, subtree: true });
    // Legacy parked reviews host may still receive late app HTML.
    const host =
      qs(document, REVIEW_HOST_SEL) ||
      qs(document, '[id^="shopify-section-"][id*="mxpr-product-reviews"]');
    if (host && host !== root) {
      obs.observe(host, { childList: true, subtree: true });
    }
  }

  function ensureAppMounts(root, tabName) {
    if (root.getAttribute('data-pdp-tabs-apps-inline') === 'true') {
      scheduleNudgeInlineApps(root, tabName);
      return;
    }
    if (tabName === 'stock') relocateStock(root);
    if (tabName === 'reviews') {
      if (!relocateReviews(root)) scheduleReviewsRetry(root);
      relocateMxprModals(root);
    }
  }

  /**
   * Merchant / explanation HTML with unclosed tags can cause the parser to
   * nest stock/wallpaper (or even reviews) inside the description panel.
   * When description is [hidden], those nested panels stay invisible.
   * Re-parent any misplaced panels back under the panels container.
   */
  function recoverNestedPanels(root) {
    const panelsRoot = qs(root, '.product-below-atc-tabs__panels');
    if (!panelsRoot) return;
    const panels = root.querySelectorAll('[data-pdp-tab-panel]');
    panels.forEach(function (panel) {
      const parentPanel = panel.parentElement && panel.parentElement.closest
        ? panel.parentElement.closest('[data-pdp-tab-panel]')
        : null;
      if (!parentPanel || parentPanel === panel) return;
      try {
        panelsRoot.appendChild(panel);
      } catch (_err) {
        /* ignore */
      }
    });
  }

  function bindTabVisibility(root) {
    const nav = qs(root, 'tabs-nav');
    if (!nav || nav.dataset.pdpTabVisBound === '1') return;
    nav.dataset.pdpTabVisBound = '1';

    // Single path: selectButton patch owns panel sync + app nudge.
    // tabs-nav:changed still fires inside original() — do not sync again there
    // (double [hidden] churn + double window.resize caused switch flicker).
    if (typeof nav.selectButton === 'function' && !nav.dataset.pdpSelectPatched) {
      nav.dataset.pdpSelectPatched = '1';
      const original = nav.selectButton.bind(nav);
      nav.selectButton = function (button, _animate) {
        // Capture before Focal toggles [hidden] — needed for Phase C overlap.
        var outgoingPanel = null;
        try {
          const prevBtn = nav.selectedButton;
          const prevId = prevBtn && prevBtn.getAttribute('aria-controls');
          if (prevId) outgoingPanel = document.getElementById(prevId);
        } catch (_prevErr) {
          outgoingPanel = null;
        }
        try {
          original(button, false);
        } catch (_err) {
          /* from/to getElementById can throw — still sync below */
        }
        if (button && nav.buttons && nav.buttons.length) {
          nav.buttons.forEach(function (btn) {
            btn.setAttribute('aria-expanded', btn === button ? 'true' : 'false');
          });
        }
        syncPanelVisibility(root, button, { overlap: true, outgoingPanel: outgoingPanel });
        if (button) ensureAppMounts(root, button.getAttribute('data-pdp-tab'));
      };
    }

    // Fallback only when selectButton could not be patched (rare).
    nav.addEventListener('tabs-nav:changed', function (event) {
      if (nav.dataset.pdpSelectPatched === '1') return;
      const button = event.detail && event.detail.button;
      syncPanelVisibility(root, button);
      if (button) ensureAppMounts(root, button.getAttribute('data-pdp-tab'));
    });
  }

  function selectTabByName(root, name) {
    const nav = qs(root, 'tabs-nav');
    const button = qs(root, '[data-pdp-tab="' + name + '"]');
    if (!nav || !button || typeof nav.selectButton !== 'function') {
      if (button) button.click();
      syncPanelVisibility(root, button);
      ensureAppMounts(root, name);
      return;
    }
    nav.selectButton(button, false);
    // Patched selectButton already synced + nudged; avoid a second pass.
    if (nav.dataset.pdpSelectPatched !== '1') {
      syncPanelVisibility(root, button);
      ensureAppMounts(root, name);
    }
  }

  function handleHash(root) {
    const hash = (window.location.hash || '').replace(/^#/, '');
    if (!hash) return;

    if (hash === 'wallpaper' || hash.indexOf('wallpaper') !== -1) {
      if (root.getAttribute('data-has-wallpaper') === 'true') {
        selectTabByName(root, 'wallpaper');
        const panel = qs(root, '[data-pdp-tab-panel="wallpaper"]');
        if (panel) {
          requestAnimationFrame(function () {
            panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          });
        }
      }
      return;
    }

    if (
      hash.indexOf('review') !== -1 ||
      hash === 'product-' + root.getAttribute('data-product-id') + '-reviews-tab'
    ) {
      selectTabByName(root, 'reviews');
    }
  }

  function observeRelocate(root) {
    const stockSource = qs(document, STOCK_SOURCE_SEL);
    const reviewsSection = findReviewsSection();

    const obs = new MutationObserver(function () {
      const stockMount = qs(root, '[data-pdp-tab-mount="stock"]');
      const reviewsMount = qs(root, '[data-pdp-tab-mount="reviews"]');

      if (
        stockSource &&
        stockMount &&
        stockMount.getAttribute('data-pdp-tab-filled') !== '1' &&
        !stockMount.querySelector('.product-form__location-stock-app, [class*="lsi-"]')
      ) {
        if (qs(stockSource, '.product-form__location-stock-app') || stockSource.childElementCount > 0) {
          relocateStock(root);
        }
      }

      // Only relocate when mount is empty; never remount (flicker).
      if (reviewsMount && !mountHasReviewContent(reviewsMount)) {
        const section = findReviewsSection();
        if (!section) return;
        const container = findReviewsContainer(section);
        const fresh = Array.prototype.slice.call(container.children).filter(function (child) {
          return !isPlaceholderNode(child);
        });
        if (fresh.length) {
          relocateReviews(root);
        }
      }
    });

    if (stockSource) {
      obs.observe(stockSource, { childList: true, subtree: true });
    }
    if (reviewsSection) {
      obs.observe(reviewsSection, { childList: true, subtree: true });
    } else {
      // Section may appear late; watch body for host attribute / id.
      obs.observe(document.body, { childList: true, subtree: true });
    }
  }

  function init() {
    const root = qs(document, '[data-product-below-atc-tabs]');
    if (!root || root.dataset.pdpTabsReady === '1') return;
    root.dataset.pdpTabsReady = '1';

    const appsInline = root.getAttribute('data-pdp-tabs-apps-inline') === 'true';

    recoverNestedPanels(root);

    if (!appsInline) {
      const stockSource = qs(document, STOCK_SOURCE_SEL);
      const reviewsSection = findReviewsSection();

      parkSource(stockSource);
      parkSource(reviewsSection);

      const stockOk = relocateStock(root);
      const reviewsOk = relocateReviews(root);

      if (!stockOk) {
        unparkSource(stockSource);
        const heading = stockSource && qs(stockSource, ':scope > .js-summary');
        if (heading) heading.removeAttribute('hidden');
      }

      // Do NOT unpark reviews on failure — keep parked + retry so the widget
      // never appears as a duplicate lower section while the tab still loads.
      if (!reviewsOk) {
        scheduleReviewsRetry(root);
      }

      observeRelocate(root);
    }

    bindTabVisibility(root);
    // Drop any leftover is-inactive / is-outgoing from older HTML/CSS, then sync.
    root.querySelectorAll('[data-pdp-tab-panel]').forEach(function (panel) {
      panel.classList.remove('is-active', 'is-inactive', 'is-outgoing');
    });
    // Init / hash: no overlap — avoid covering the default tab on first paint.
    syncPanelVisibility(root, null, { overlap: false });

    handleHash(root);
    syncPanelVisibility(root, null, { overlap: false });
    // Pin height from in-flow active panel, then lock absolute stack for switches.
    settlePanelsMinHeight(root);
    root.setAttribute('data-pdp-tabs-stacked', '1');
    settlePanelsMinHeight(root);
    scheduleNudgeInlineApps(root, root.getAttribute('data-default-tab') || 'reviews');
    relocateMxprModals(root);
    observeMxprModals(root);

    window.addEventListener('hashchange', function () {
      handleHash(root);
      syncPanelVisibility(root, null, { overlap: false });
    });

    // bfcache / back-forward: previously viewed PDPs can restore pre-#297 HTML+JS.
    // Re-sync panels and nudge inline apps so stock/reviews are not left blank.
    window.addEventListener('pageshow', function (event) {
      if (!event.persisted && root.dataset.pdpTabsReady === '1') {
        /* still re-sync after soft navigations that reuse the element */
      }
      recoverNestedPanels(root);
      root.querySelectorAll('[data-pdp-tab-panel]').forEach(function (panel) {
        panel.classList.remove('is-active', 'is-inactive', 'is-outgoing');
      });
      syncPanelVisibility(root, null, { overlap: false });
      settlePanelsMinHeight(root);
      root.setAttribute('data-pdp-tabs-stacked', '1');
      settlePanelsMinHeight(root);
      const active =
        qs(root, '.tabs-nav__item[aria-expanded="true"]') ||
        qs(root, '[data-pdp-tab="' + (root.getAttribute('data-default-tab') || 'reviews') + '"]');
      if (active) scheduleNudgeInlineApps(root, active.getAttribute('data-pdp-tab'), { force: true });
      relocateMxprModals(root);
    });

    document.addEventListener(
      'click',
      function (event) {
        const link = event.target.closest && event.target.closest('a[href="#wallpaper"], a[href*="#wallpaper"]');
        if (!link) return;
        if (root.getAttribute('data-has-wallpaper') !== 'true') return;
        selectTabByName(root, 'wallpaper');
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector && event.target.querySelector('[data-product-below-atc-tabs]')) {
      const root = qs(event.target, '[data-product-below-atc-tabs]');
      if (root) {
        root.dataset.pdpTabsReady = '';
        root.dataset.pdpReviewsRetrying = '';
        root._pdpNudgedTabs = null;
        if (root._pdpHeightRaf) {
          cancelAnimationFrame(root._pdpHeightRaf);
          root._pdpHeightRaf = null;
        }
        if (root._pdpOverlapRaf1) {
          cancelAnimationFrame(root._pdpOverlapRaf1);
          root._pdpOverlapRaf1 = null;
        }
        if (root._pdpOverlapRaf2) {
          cancelAnimationFrame(root._pdpOverlapRaf2);
          root._pdpOverlapRaf2 = null;
        }
        clearOutgoingOverlaps(root);
        const panelsRoot = qs(root, '.product-below-atc-tabs__panels');
        if (panelsRoot) panelsRoot.style.removeProperty('min-height');
        const nav = qs(root, 'tabs-nav');
        if (nav) {
          nav.dataset.pdpTabVisBound = '';
          nav.dataset.pdpSelectPatched = '';
        }
      }
      init();
    }
    // Reviews apps section loaded/reloaded later
    if (event.target && (event.target.matches && event.target.matches(REVIEW_HOST_SEL) || (event.target.id && event.target.id.indexOf('mxpr-product-reviews') !== -1))) {
      const root = qs(document, '[data-product-below-atc-tabs]');
      if (root) {
        root.dataset.pdpReviewsRetrying = '';
        parkSource(event.target);
        if (!relocateReviews(root)) scheduleReviewsRetry(root);
      }
    }
  });
})();
